import React, { useState } from "react";
import { Collapse, Button, Tag, Pagination, Empty, Spin, Input } from "antd";
import {
  PhoneOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  EyeOutlined,
  SearchOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { LoadingOutlined } from "@ant-design/icons";
import NumberFormat from "react-number-format";

const CompletedOrdersList = ({
  orders,
  loading,
  pagination,
  onViewOrder,
  updateFilters,
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  const newAntIcon = (
    <LoadingOutlined style={{ fontSize: 24, color: "#008060" }} spin />
  );

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "DELIVERED":
        return "green";
      case "RETURNED":
        return "red";
      default:
        return "default";
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    updateFilters({ page: 1, search: value });
  };

  const handlePaginationChange = (page, pageSize) => {
    updateFilters({ page, limit: pageSize });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  if (loading) {
    return <Spin indicator={newAntIcon} />;
  }

  return (
    <div>
      <div className="mb-4">
        <Input
          placeholder="Search by order ref or buyer name..."
          prefix={<SearchOutlined />}
          value={searchTerm}
          onChange={handleSearch}
          allowClear
        />
      </div>

      {orders && orders.length > 0 ? (
        <>
          {/* Desktop View */}
          <div className="hidden md:block">
            <table className="w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border border-gray-300 p-3 text-left">Date</th>
                  <th className="border border-gray-300 p-3 text-left">
                    Order Ref
                  </th>
                  <th className="border border-gray-300 p-3 text-left">
                    Restaurant
                  </th>
                  <th className="border border-gray-300 p-3 text-left">
                    Buyer Name
                  </th>
                  <th className="border border-gray-300 p-3 text-left">
                    Status
                  </th>
                  <th className="border border-gray-300 p-3 text-left">
                    Delivery Fee
                  </th>
                  <th className="border border-gray-300 p-3 text-center">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr
                    key={order.id}
                    className="border border-gray-300 hover:bg-gray-50"
                  >
                    <td className="border border-gray-300 p-3">
                      <div className="flex items-center gap-2">
                        <CalendarOutlined className="text-gray-400" />
                        {formatDate(order.createdAt || order.completedAt)}
                      </div>
                    </td>
                    <td className="border border-gray-300 p-3 font-mono">
                      {order.orderCode || order.order?.orderCode || "N/A"}
                    </td>
                    <td className="border border-gray-300 p-3">
                      {order.businessName || order.business?.name || "N/A"}
                    </td>
                    <td className="border border-gray-300 p-3">
                      {order.buyerName || order.customer?.name || "N/A"}
                    </td>
                    <td className="border border-gray-300 p-3">
                      <Tag
                        color={getStatusColor(
                          order.status || order.delivery?.deliveryStatus,
                        )}
                      >
                        {order.status ||
                          order.delivery?.deliveryStatus ||
                          "DELIVERED"}
                      </Tag>
                    </td>
                    <td className="border border-gray-300 p-3">
                      <NumberFormat
                        thousandSeparator={true}
                        prefix="₦"
                        displayType="text"
                        value={order.deliveryFee || 0}
                      />
                    </td>
                    <td className="border border-gray-300 p-3 text-center">
                      <Button
                        type="primary"
                        size="small"
                        onClick={() => onViewOrder(order)}
                        icon={<EyeOutlined />}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile View */}
          <div className="md:hidden">
            <Collapse>
              {orders.map((order) => (
                <Collapse.Panel
                  key={order.id}
                  header={
                    <div className="flex justify-between items-center w-full gap-2">
                      <span className="font-medium ">
                        {formatDate(order?.order?.createdAt)}
                      </span>
                      <span className="font-mono ">
                        {order.orderCode || order.order?.orderCode}
                      </span>
                      <Tag
                        color={getStatusColor(
                          order.status || order.delivery?.deliveryStatus,
                        )}
                        className="m-0"
                      >
                        {order.status ||
                          order.delivery?.deliveryStatus ||
                          "DELIVERED"}
                      </Tag>
                    </div>
                  }
                >
                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <CalendarOutlined className="text-gray-400 mt-1" />
                      <div>
                        <div className=" text-gray-500">Date:</div>
                        <div className="font-medium">
                          {formatDate(order?.order.createdAt)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className=" text-gray-500 min-w-fit">
                        Order Ref:
                      </span>
                      <span className="font-mono">
                        {order.orderCode || order.order?.orderCode}
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className=" text-gray-500 min-w-fit">
                        Restaurant:
                      </span>
                      <span className="font-medium">
                        {order.businessName || order.business?.name}
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className=" text-gray-500 min-w-fit">
                        Buyer Name:
                      </span>
                      <span className="font-medium">
                        {order.buyerName || order.customer?.name || "N/A"}
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className=" text-gray-500 min-w-fit">Status:</span>
                      <Tag
                        color={getStatusColor(
                          order.status || order.delivery?.deliveryStatus,
                        )}
                      >
                        {order.status ||
                          order.delivery?.deliveryStatus ||
                          "DELIVERED"}
                      </Tag>
                    </div>

                    {!order.delivery?.restaurantAcceptedDelivery && (
                      <div className="flex items-start gap-2">
                        <DollarOutlined className="text-gray-400 mt-1" />
                        <div>
                          <div className="text-sm text-gray-500">
                            Delivery Fee:
                          </div>
                          <div className="font-medium">
                            <NumberFormat
                              thousandSeparator={true}
                              prefix="₦"
                              displayType="text"
                              value={parseFloat(
                                order?.deliveryFeeEarnings
                                  ?.deliveryFeeKonnect || 0,
                              ).toFixed(2)}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="mt-4">
                      <Button
                        type="primary"
                        block
                        onClick={() => onViewOrder(order)}
                        icon={<EyeOutlined />}
                      >
                        View Order Details
                      </Button>
                    </div>
                  </div>
                </Collapse.Panel>
              ))}
            </Collapse>
          </div>

          <Pagination
            current={pagination?.page || 1}
            pageSize={pagination?.limit || 10}
            total={pagination?.total || 0}
            onChange={handlePaginationChange}
            showSizeChanger
            showQuickJumper
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} of ${total} orders`
            }
            style={{ marginTop: 16, textAlign: "center" }}
          />
        </>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No completed orders"
        />
      )}
    </div>
  );
};

export default CompletedOrdersList;
